/**
 * Минимальный хук серверного состояния. Никакой библиотеки стора: истина
 * бизнеса живёт на сервере, клиент держит только состояние запроса.
 *
 * Различает состояния из матрицы дизайн-системы:
 *   `loading`   — первая загрузка, данных ещё нет;
 *   `refresh`   — фоновое обновление, прежний результат ОСТАЁТСЯ на экране;
 *   `error`     — ошибка, прежний результат сохраняется, если он был;
 *   `ready`     — данные актуальны.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./client";

export type RequestState = "loading" | "refresh" | "ready" | "error";

export interface RequestResult<T> {
  data: T | null;
  error: ApiError | null;
  state: RequestState;
  /** Данные показаны, но относятся к прошлому запросу (идёт обновление). */
  stale: boolean;
  reload: () => void;
}

export function useRequest<T>(
  load: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): RequestResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [state, setState] = useState<RequestState>("loading");
  const [nonce, setNonce] = useState(0);
  const hasData = useRef(false);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setState(hasData.current ? "refresh" : "loading");

    loadRef
      .current()
      .then((result) => {
        if (cancelled) return;
        // Замена атомарная: прежний результат живёт до прихода нового.
        hasData.current = true;
        setData(result);
        setError(null);
        setState("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError("unknown_error", String(cause), 0),
        );
        setState("error");
      });

    return () => {
      cancelled = true;
    };
    // Зависимости задаёт вызывающий: перезапрос делает смена контекста и
    // ключей экрана, а не пересоздание функции загрузки на каждый рендер —
    // поэтому сама `load` живёт в ref и в список зависимостей не входит.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return {
    data,
    error,
    state,
    stale: state === "refresh" || (state === "error" && data !== null),
    reload,
  };
}
